import './UnverifiedUser.sass'

import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react'
import { useResendChangeEmail, useUserStatus, useVerifyUser } from 'dataQueries'

import { BKBNThemeWrapper } from 'components/common/BKBNThemeWrapper'
import { EmailVerifyMode } from '../EmailVerified/EmailVerified'
import { OnboardingPageVerifyEmail } from '../OnboardingUser/OnboardingPageVerifyEmail.component'
import { PageTransition } from 'utils/animations'
import { logAnalyticsEvent } from 'utils/analytics'
import { isRateLimitError } from 'utils/APIDecideErrorType'
import { useAuth0 } from 'utils/auth'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'

const UnverifiedUser: React.FC = (props: any) => {

  const { t } = useTranslation(['unverified_user'])
  const { refreshUser, user } = useAuth0()

  const params = useMemo(() => new URLSearchParams(window.location.search), [])
  const isChangeEmail = useMemo(() => params.get('mode') === EmailVerifyMode.CHANGE_EMAIL, [params])

  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()
  const resendChangeEmail = useResendChangeEmail()
  const verifyUser = useVerifyUser()
  const userStatusRequest = useUserStatus()

  // log entering this page
  useEffect(() => {
    logAnalyticsEvent('enters_user_unverified_screen', {})
  }, [])

  // start long polling for user status
  useEffect(() => {
    window.setTimeout(() => {
      userStatusRequest.refetch()
    }, 2000)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // handle user status
  useLayoutEffect(() => {
    if (
      userStatusRequest.isSuccess &&
      userStatusRequest.data?.isEmailVerified &&
      !user?.email_verified
    ) {
      logAnalyticsEvent('email_verified', {})
      refreshUser()
    } else if (
      (
        userStatusRequest.isSuccess ||
        userStatusRequest.isError
      ) &&
      !userStatusRequest.data?.isEmailVerified
    ) {
      window.setTimeout(() => {
        userStatusRequest.refetch()
      }, 2000)
    }
  }, [userStatusRequest, user, refreshUser])

  const handleResendEmail = useCallback(() => {
    if (isChangeEmail) return resendChangeEmail.mutate({}, {
      onSuccess: () => {
        spawnSuccessToast(t('resend_success'))
      },
      onError: (error) => {
        if (isRateLimitError(error)) spawnErrorToast(t('resend_too_many_attempts'))
      },
    })

    return verifyUser.mutate({}, {
      onSuccess: () => {
        spawnSuccessToast(t('resend_success'))
      },
    })
    // skip deps t, spawnSuccessToast, spawnErrorToast
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChangeEmail, resendChangeEmail, verifyUser])

  return (
    <BKBNThemeWrapper>
      <PageTransition>
        <OnboardingPageVerifyEmail handleResendEmail={handleResendEmail} />
      </PageTransition>
    </BKBNThemeWrapper>
  )
}

export default UnverifiedUser
